import { formatCurrency } from '@airship-platform/airship-patterns';
import {
  FlexBox,
  ItemSelector,
  Spacer,
} from '@airship-platform/jam-web/components';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { ProductPrice } from '../../data-access';

interface PriceSectionEditableProps {
  prices: ProductPrice[];
  defaultPriceType: ProductPrice['priceType'];
  canEditPrice?: boolean;
  isEditable?: boolean;
  isDisabled?: boolean;
  displayPriceOptions?: boolean;
  hasMembership?: boolean;
  quantityComponent?: React.ReactNode;
  startAdornment?: string;
  quantity?: number;
  showFullPrice?: boolean;
  onUpdateProduct?: (changes: {
    price: number;
    defaultPriceType: ProductPrice['priceType'];
  }) => void;
  onPriceValidityChange?: (isPriceValid: boolean) => void;
}

const isValidPriceInput = (value: string) =>
  value !== '' && Number.isFinite(+value);

export const PriceSection: React.FC<{
  value: number;
  quantity?: number;
  prefix?: string;
  currency?: 'USD';
  hasDiscount?: boolean;
  valueWithDiscount?: number;
}> = ({
  prefix = 'Price: ',
  value,
  quantity = 1,
  currency,
  hasDiscount,
  valueWithDiscount,
}) =>
  hasDiscount && valueWithDiscount ? (
    <Fragment>
      <Typography variant="subtitle1" sx={{ textDecoration: 'line-through' }}>
        {`${formatCurrency(value * quantity, currency)}`}
      </Typography>
      <Typography variant="subtitle1" color="secondary">
        {`${formatCurrency(valueWithDiscount * quantity, currency)}`} member
        price
      </Typography>
    </Fragment>
  ) : (
    <Typography variant="subtitle1">
      {`${prefix}${formatCurrency(value * quantity, currency)}`}
    </Typography>
  );

const PriceSectionEditable: React.FC<PriceSectionEditableProps> = ({
  prices,
  defaultPriceType,
  isEditable,
  isDisabled,
  displayPriceOptions,
  canEditPrice,
  hasMembership,
  quantityComponent,
  startAdornment,
  quantity = 1,
  showFullPrice = false,
  onUpdateProduct,
  onPriceValidityChange,
}) => {
  const filteredPrices = prices
    .filter((price) => price.value > 0)
    .filter((price) => ['STANDARD', 'MEMBER'].includes(price.priceType));

  const defaultPrice = useMemo(
    () =>
      prices.find((p) => p.priceType === defaultPriceType) || {
        value: 0,
        currency: 'USD' as const,
        priceType: defaultPriceType,
        taxable: true,
      },
    [prices, defaultPriceType]
  );

  const shouldShowPriceOptions =
    filteredPrices.length > 0 && displayPriceOptions;

  const [currentPrice, setCurrentPrice] = useState<ProductPrice>(defaultPrice);
  const [inputValue, setInputValue] = useState<string>(
    defaultPrice.value.toString()
  );
  const isPriceValid = isValidPriceInput(inputValue);

  useEffect(() => {
    setCurrentPrice(defaultPrice);
    if (prices.some((p) => p.priceType === defaultPriceType)) {
      setInputValue(defaultPrice.value.toString());
    }
  }, [defaultPrice, prices, defaultPriceType]);

  useEffect(() => {
    if (onPriceValidityChange) {
      onPriceValidityChange(isPriceValid);
    }
  }, [isPriceValid, onPriceValidityChange]);

  const addonPrice = prices.find((price) => {
    const base = currentPrice.priceType.replace('_ADDON', '');
    return price.priceType === `${base}_ADDON` && price.value > 0;
  });

  const editable = isEditable && canEditPrice && !isDisabled;

  const handleProductUpdate = (changes: {
    price: number;
    defaultPriceType: ProductPrice['priceType'];
  }) => onUpdateProduct && onUpdateProduct(changes);

  const onPriceTypeChange = (price: ProductPrice) => {
    setCurrentPrice(price);
    setInputValue(price.value.toString());
    handleProductUpdate({
      price: price.value,
      defaultPriceType: price.priceType,
    });
  };

  const onToggleChange = () => {
    const newPrice =
      addonPrice && currentPrice.priceType !== addonPrice.priceType
        ? addonPrice
        : filteredPrices.find(
            (price) =>
              price.priceType === currentPrice.priceType.replace('_ADDON', '')
          ) || defaultPrice;

    setCurrentPrice(newPrice);
    setInputValue(newPrice.value.toString());
    handleProductUpdate({
      price: newPrice.value,
      defaultPriceType: newPrice.priceType,
    });
  };

  const onPriceInputChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    if (/^-?\d*\.?\d*$/.test(e.target.value)) {
      const sanitized = e.target.value.replace(/^(-?)0+(?=\d)/, '$1');
      setInputValue(sanitized);
      if (isValidPriceInput(sanitized)) {
        handleProductUpdate({
          price: +sanitized,
          defaultPriceType: 'MANUAL',
        });
      }
    }
  };

  return (
    <FlexBox flexDirection={'column'} gap={3} width={'100%'}>
      <FlexBox justifyContent={'space-between'} gap={2}>
        <FlexBox gap={2} alignItems={'center'}>
          {editable ? (
            <TextField
              type="text"
              value={inputValue}
              onChange={(e) => onPriceInputChange(e)}
              error={!isPriceValid}
              helperText={
                isPriceValid ? (
                  <span>&nbsp;</span>
                ) : (
                  'Please enter a valid price'
                )
              }
              slotProps={{
                input: {
                  inputMode: 'decimal',
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="body2" color="textPrimary">
                        {startAdornment || '$'}
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }}
            />
          ) : (
            <PriceSection
              currency={currentPrice.currency}
              prefix={startAdornment}
              value={currentPrice.value}
              quantity={showFullPrice && quantity ? quantity : 1}
              hasDiscount={currentPrice.hasDiscount ?? false}
              valueWithDiscount={currentPrice.valueWithDiscount ?? 0}
            />
          )}
        </FlexBox>
        <FlexBox flexDirection={'column'}>
          <Spacer height="0.5rem" />
          {quantityComponent}
        </FlexBox>
      </FlexBox>

      {shouldShowPriceOptions && (
        <>
          <Divider />
          <FlexBox flexDirection={'column'} gap={2}>
            <Typography variant="subtitle2">Select price</Typography>
            {hasMembership && (
              <FlexBox gap={2}>
                {filteredPrices.map((price) => (
                  <ItemSelector
                    key={price.priceType}
                    label={
                      price.priceType.charAt(0).toUpperCase() +
                      price.priceType.slice(1).toLowerCase()
                    }
                    value={price.priceType}
                    checked={
                      currentPrice.priceType === price.priceType ||
                      currentPrice.priceType === `${price.priceType}_ADDON`
                    }
                    type={'radio'}
                    disabled={isDisabled}
                    handleChange={() => onPriceTypeChange(price)}
                  />
                ))}
              </FlexBox>
            )}
          </FlexBox>
        </>
      )}

      {displayPriceOptions && (
        <FormControlLabel
          control={
            <Switch
              checked={
                !!addonPrice && currentPrice.priceType === addonPrice.priceType
              }
              color="tertiary"
              disabled={isDisabled || !addonPrice}
              onChange={onToggleChange}
            />
          }
          label={addonPrice ? 'Add-on' : 'Add-on (no add-on price available)'}
        />
      )}
    </FlexBox>
  );
};

export default PriceSectionEditable;
